































import { OElement } from '../element';
import { DateTime } from 'luxon';
import { OInput } from '../input';
import Vue from 'vue';

export default Vue.extend({
  name: 'ODatepicker',
  model: {
    prop: 'vModelValue',
    event: 'change',
  },
  components: {
    OElement,
    OInput,
  },
  props: {
    id: String,
    vModelValue: String,
  },
  watch: {},
  data() {
    return {
      options: {
        zone: 'Europe/Stockholm',
        locale: 'sv',
      },

      currentValue: this.vModelValue,
      parsedValue: undefined as DateTime | undefined,
      calendarValue: undefined as DateTime | undefined,

      isValid: true,
      isOpen: false,
    };
  },
  created(): void {
    this.emitFormattedValue();
  },
  methods: {
    /**
     * Listeners
     */
    onBlur(): void {
      this.emitFormattedValue();
    },
    onClick(): void {
      this.isOpen = !this.isOpen;
      this.calendarValue = this.parsedValue;
    },
    onClickDay(dateTime: DateTime): void {
      const clicked = dateTime.toLocaleString();
      if (clicked) {
        this.isOpen = false;

        this.currentValue = clicked;
        this.emitFormattedValue();
      }
    },
    onKeyUp(event: KeyboardEvent): void {
      switch (event.code) {
        case 'Enter':
          this.onClick();
          break;
        case 'Escape':
          this.isOpen = false;
          break;
        default:
          break;
      }
    },
    /**
     * Updates
     */
    updateParsedValue(): void {
      if (this.isValidDate(this.currentValue)) this.parsedValue = DateTime.fromISO(this.currentValue, this.options);
    },
    updateCalendarValue(): void {
      if (this.isValidDate(this.currentValue)) this.calendarValue = DateTime.fromISO(this.currentValue, this.options);
    },
    emitFormattedValue(): void {
      const formattedDate = this.formatDate(this.currentValue);
      this.currentValue = formattedDate ?? this.currentValue;

      this.updateParsedValue();
      this.updateCalendarValue();

      this.$emit('change', this.currentValue);
    },
    /**
     * Formatting
     */
    formatDate(value: string): string | undefined {
      if (this.isValidDate(value)) {
        const dateTime = DateTime.fromISO(value, this.options);

        return dateTime.toLocaleString() ?? undefined;
      }
      return undefined;
    },
    getDayName(dateTime: DateTime): string {
      if (dateTime) {
        return dateTime.toFormat('ccc');
      }
      return 'Unknown';
    },
    /**
     * Validation
     */
    isValidDate(value: unknown): boolean {
      if (typeof value === 'string') {
        return DateTime.fromISO(value, this.options).isValid;
      }
      return false;
    },
    isToday(dateTime: DateTime): boolean {
      const today = DateTime.local();
      return dateTime.hasSame(today, 'day');
    },
    isCurrent(dateTime: DateTime): boolean {
      if (this.parsedValue) {
        return dateTime.hasSame(this.parsedValue, 'day');
      }
      return false;
    },
    isOutsideMonth(dateTime: DateTime, currentDateTime: DateTime): boolean {
      if (this.calendarValue) {
        return (
          dateTime.startOf('month') < currentDateTime.startOf('month') ||
          dateTime.endOf('month') > currentDateTime.endOf('month')
        );
      }
      return false;
    },
  },
  computed: {
    year(): number | undefined {
      return this.calendarValue?.year ?? undefined;
    },
    month(): string | undefined {
      return this.calendarValue?.monthLong ?? undefined;
    },
    dateTimes(): DateTime[] {
      const dateTimes = [];
      const daysInMonth = this.calendarValue?.daysInMonth ?? 0;

      /**
       * Previous month
       */
      const startOfMonth = this.calendarValue?.startOf('month');
      const firstDayOfCurrentMonth = Number(startOfMonth?.toFormat('c'));
      const startOfWeek = startOfMonth?.minus({ days: firstDayOfCurrentMonth - 1 });
      let nextDay = startOfWeek;

      for (let before = 0; before < firstDayOfCurrentMonth - 1; before++) {
        if (nextDay) {
          dateTimes.push(nextDay);
          nextDay = nextDay.plus({ days: 1 });
        }
      }
      /**
       * Current month
       */
      for (let day = 1; day <= daysInMonth; day++) {
        const date = this.calendarValue?.set({ day });
        if (date) dateTimes.push(date);
      }

      /**
       * Next month
       */
      const endOfMonth = this.calendarValue?.endOf('month');
      const lastDayOfCurrentMonth = Number(endOfMonth?.toFormat('c'));
      const endOfWeek = endOfMonth?.plus({ days: 1 });
      nextDay = endOfWeek;

      for (let after = 0; after < 7 - lastDayOfCurrentMonth; after++) {
        if (nextDay) {
          dateTimes.push(nextDay);
          nextDay = nextDay.plus({ days: 1 });
        }
      }

      return dateTimes;
    },
    listeners(): Record<
      string,
      ((event: KeyboardEvent) => void) | ((event: Event) => void) | ((event: Event) => void)[]
    > {
      return {
        ...this.$listeners,
        blur: this.onBlur,
        click: this.onClick,
        keyup: this.onKeyUp,
        change: () => undefined,
      };
    },
    vModel: {
      get(): unknown {
        return this.currentValue ?? '';
      },
      set(value: string): void {
        this.currentValue = value ?? '';
        this.isValid = this.isValidDate(this.currentValue);

        this.updateParsedValue();

        this.$emit('change', this.currentValue);
      },
    },
  },
});
